import { toast } from 'sonner'
import { Link } from '@tanstack/react-router'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ActionPopover } from '@/components/action-popover'
import { ReceiptText } from 'lucide-react'
import { useUpdateInvoiceExpectation } from '@/features/purchase-orders/hooks'
import { formatApiErrorMessage } from '@/lib/api-error'
import { formatDateOnly, formatMoney, formatStatus, parseUTCDate } from '@/lib/utils'
import type { components } from '@/api/schema'

type InvoiceExpectation = components['schemas']['ManufacturerInvoiceExpectationRead']

type ManufacturerInvoiceLink = components['schemas']['ManufacturerInvoicePOLinkRead']

function formatInvoicePaymentTerms(expectation: InvoiceExpectation): string {
  const percent = Number(expectation.payment_terms_discount_percent)
  if (
    Number.isFinite(percent) &&
    expectation.payment_terms_discount_percent != null &&
    expectation.payment_terms_discount_days != null &&
    expectation.payment_terms_net_days != null
  ) {
    return `${percent.toLocaleString()}% ${expectation.payment_terms_discount_days}, Net ${expectation.payment_terms_net_days}`
  }
  if (expectation.payment_terms_net_days != null) {
    return `Net ${expectation.payment_terms_net_days}`
  }
  const labels: Record<string, string> = {
    prepaid: 'Prepaid',
    due_on_receipt: 'Due on receipt',
    proximo: '10th proximo',
    unknown: 'Not parsed',
  }
  return expectation.payment_terms_type
    ? labels[expectation.payment_terms_type] || formatStatus(expectation.payment_terms_type)
    : 'Not recorded'
}

export function InvoiceExpectationPanel({
  poUuid,
  expectation,
  invoiceLinks,
}: {
  poUuid: string
  expectation: InvoiceExpectation
  invoiceLinks: ManufacturerInvoiceLink[]
}) {
  const mutation = useUpdateInvoiceExpectation(poUuid)
  const terminalAt = expectation.waived_at ?? expectation.cancelled_at
  const terminalBy = expectation.waived_by ?? expectation.cancelled_by
  const terminalReason = expectation.waiver_reason ?? expectation.cancellation_reason
  const activeInvoiceLinks = invoiceLinks.filter(
    (link) =>
      !link.invoice.archived_at &&
      !['dismissed', 'duplicate', 'superseded', 'archived'].includes(link.invoice.status) &&
      !['confirmed_duplicate', 'superseded'].includes(link.invoice.duplicate_status),
  )

  return (
    <Card className="gap-0 py-0">
      <CardContent className="space-y-3 p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <ReceiptText className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold">Expected invoice</h3>
          </div>
          <Badge
            variant={
              expectation.status === 'cancelled'
                ? 'destructive'
                : expectation.status === 'received'
                  ? 'default'
                  : expectation.status === 'waived'
                    ? 'outline'
                    : 'secondary'
            }
          >
            {formatStatus(expectation.status)}
          </Badge>
        </div>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-3">
          <div>
            <dt className="text-xs text-muted-foreground">Expected</dt>
            <dd className="font-semibold tabular-nums">{formatMoney(expectation.po_total)}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Billed</dt>
            <dd className="font-semibold tabular-nums">{formatMoney(expectation.billed_amount)}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Remaining</dt>
            <dd className="font-semibold tabular-nums">
              {formatMoney(expectation.remaining_amount)}
            </dd>
          </div>
          <div className="sm:col-span-1">
            <dt className="text-xs text-muted-foreground">Expected since</dt>
            <dd className="font-medium">
              {parseUTCDate(expectation.expected_at).toLocaleDateString()}
            </dd>
          </div>
          <div className="col-span-2 sm:col-span-2">
            <dt className="text-xs text-muted-foreground">Payment terms</dt>
            <dd className="font-medium">{formatInvoicePaymentTerms(expectation)}</dd>
          </div>
          {terminalAt && (
            <div className="col-span-2 border-t pt-2">
              <dt className="text-xs text-muted-foreground">
                {expectation.status === 'waived' ? 'Waived' : 'Cancelled'}
              </dt>
              <dd className="font-medium">
                {parseUTCDate(terminalAt).toLocaleDateString()}
                {terminalBy ? ` by ${terminalBy}` : ''}
              </dd>
              {terminalReason && <dd className="mt-1 text-muted-foreground">{terminalReason}</dd>}
            </div>
          )}
        </dl>
        {activeInvoiceLinks.length > 0 && (
          <div className="divide-y border-t">
            {activeInvoiceLinks.map((link) => {
              const allocated =
                link.allocation_amount ??
                (link.allocation_percent != null
                  ? (Number(expectation.po_total) * Number(link.allocation_percent)) / 100
                  : activeInvoiceLinks.length === 1 && Number(expectation.billed_amount) > 0
                    ? expectation.billed_amount
                    : null)
              return (
                <div
                  key={link.id}
                  className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm"
                >
                  <div className="min-w-0">
                    <Link
                      to="/invoices/$invoiceId"
                      params={{ invoiceId: link.invoice.uuid }}
                      className="font-medium text-primary hover:underline"
                    >
                      {link.invoice.invoice_number || `Invoice ${link.invoice.id}`}
                    </Link>
                    {link.invoice.invoice_date && (
                      <span className="ml-2 text-xs text-muted-foreground">
                        {formatDateOnly(link.invoice.invoice_date)}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 tabular-nums">
                    <span className="text-muted-foreground">
                      Invoice {formatMoney(link.invoice.invoice_total)}
                    </span>
                    <span className="font-medium">
                      {allocated == null ? 'Allocation needed' : `${formatMoney(allocated)} billed`}
                    </span>
                  </div>
                </div>
              )
            })}
          </div>
        )}
        {expectation.status === 'expected' && (
          <div className="flex gap-2 border-t pt-3">
            {(['waive', 'cancel'] as const).map((action) => (
              <ActionPopover
                key={action}
                trigger={
                  <Button variant={action === 'waive' ? 'outline' : 'ghost'} size="sm">
                    {action === 'waive' ? 'Waive' : 'Cancel expectation'}
                  </Button>
                }
                title={action === 'waive' ? 'Waive expected invoice' : 'Cancel expected invoice'}
                note={{
                  label: 'Reason',
                  placeholder:
                    action === 'waive'
                      ? 'Why will no manufacturer invoice come?'
                      : 'Why was it cancelled?',
                  minLength: 3,
                }}
                confirmLabel={action === 'waive' ? 'Waive expectation' : 'Cancel expectation'}
                confirmVariant={action === 'cancel' ? 'destructive' : 'default'}
                pending={mutation.isPending && mutation.variables?.action === action}
                pendingLabel="Saving..."
                onConfirm={(reason, close) =>
                  mutation.mutate(
                    { action, reason },
                    {
                      onSuccess: () => {
                        toast.success(
                          action === 'waive'
                            ? 'Invoice expectation waived'
                            : 'Invoice expectation cancelled',
                        )
                        close()
                      },
                      onError: (error) =>
                        toast.error(
                          formatApiErrorMessage('Could not update invoice expectation', error),
                        ),
                    },
                  )
                }
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
